import { FormControl } from '@angular/forms';

/**
 * Constant representing the name of the custom fields array filled in with the control form groups.
 * @const {string}
 */
export const ORDER_CUSTOM_FIELDS_CONTROL_NAME = 'customFields';

/**
 * Constant representing sending order emails to individual clients.
 * @type {string}
 */
export const SEND_ORDER_EMAILS_TO_INDIVIDUAL_CLIENT: string = 'send_order_emails_to_individual_client';

/**
 * Constant representing sending order emails to clients.
 * @type {string}
 */
export const SEND_ORDER_EMAILS_TO_CLIENT: string = 'send_order_emails_to_client';

/**
 * Constant representing sending order emails to owners.
 * @type {string}
 */
export const SEND_ORDER_EMAILS_TO_OWNER: string = 'send_order_emails_to_owner';

/**
 * Constant representing the Social Insurance Number.
 * @type {string}
 */
export const SIN: string = 'sin';

/**
 * Constant representing a combination of the provincial sales tax portion.
 * and the GST in some Canadian provinces.
 * @type {string}
 */
export const HST_GST_QST_PST: string = 'hst_gst_qst_pst';

/**
 * Constant representing the Canadian ACTRA union membership number.
 * @type {string}
 */
export const ACTRA_MEMBERSHIP_NUMBER: string = 'actra_membership_number';

/**
 * Constant representing whether the Canadian ACTRA union member is in a good standing.
 * @type {string}
 */
export const ACTRA_MEMBER_IN_GOOD_STANDING: string = 'actra_member_in_good_standing';

/**
 * Constant representing whether the Canadian ACTRA member is an apprentice.
 * @type {string}
 */
export const ACTRA_MEMBER: string = 'actra_member';

/**
 * Constant representing the Social Security Number.
 * @type {string}
 */
export const SSN: string = 'ssn';

/**
 * Constant representing the American bank transit number.
 * @type {string}
 */
export const TRANSIT_NUMBER: string = 'transit_number';

/**
 * Constant representing the American bank financial institution number.
 * @type {string}
 */
export const FINANCIAL_INSTITUTION_NUMBER: string = 'financial_institution_number';

/**
 * Constant representing the American bank account number.
 * @type {string}
 */
export const ACCOUNT_NUMBER: string = 'account_number';

/**
 * Constant representing whether the Canadian ACTRA member is a permitee.
 * @type {string}
 */
export const PERMITTEE: string = 'permittee';

/**
 * Constant representing the Canadian ACTRA member loan-out or LLC name.
 * @type {string}
 */
export const LOAN_OUT_OR_LLC_NAME: string = 'loan_out_or_llc_name';

/**
 * Constant representing the Canadian ACTRA member BN (Loan-outs or LLCs) number.
 * @type {string}
 */
export const BN_LOAN_OUTS_OR_LLCS: string = 'bn_loan_outs_or_llcs';

/**
 * Constant representing the American EIN (Loan-outs or LLCs) number.
 * @type {string}
 */
export const EIN_LOAN_OUTS_OR_LLCS: string = 'ein_loan_outs_or_llcs';

/**
 * Constant representing the American AFTRA membership number.
 * @type {string}
 */
export const SAG_AFTRA_MEMBERSHIP_NUMBER: string = 'sag_aftra_membership_number';

/**
 * Constant representing the American ABA bank routing number.
 * @type {string}
 */
export const ABA_ROUTING_NUMBER: string = 'aba_routing_number';

/**
 * Constant representing the American account number.
 * @type {string}
 */
export const US_ACCOUNT_NUMBER: string = 'us_account_number';

/**
 * Constant representing whether the agent fee should be added to the American payment.
 * @type {string}
 */
export const ADD_AGENT_FEE: string = 'add_agent_fee';

/**
 * Constant representing whether the cast collaboration fee should be added to the American payment.
 * @type {string}
 */
export const ADD_CAST_COLLABORATION_FEE: string = 'add_cast_collaboration_fee';

/**
 * Constant representing the place of performance in a Canadian payment.
 * @type {string}
 */
export const PLACE_OF_PERFORMANCE: string = 'place_of_performance';

/**
 * Constant representing the nature of production in a Canadian payment.
 * @type {string}
 */
export const ACTRA_NATURE_OF_PRODUCTION: string = 'actra_nature_of_production';

/**
 * Constant representing the performance category in a Canadian payment.
 * @type {string}
 */
export const ACTRA_PERFORMANCE_CATEGORY: string = 'actra_performance_category';

/**
 * Constant representing the signatory that payer selects and that dictates the American H&R fee.
 * @type {string}
 */
export const PAYER_SIGNATORY_OPTIONS: string = 'payer_signatory_options';

/**
 * Constant representing the payment signatory.
 * @type {string}
 */
export const PAYMENT_SIGNATORY: string = 'payment_signatory';

/**
 * Constant representing the talent display option in the payment for performance.
 * @type {string}
 */
export const PERFORMER_PAYMENT_DISPLAY: string = 'performer_payment_display';

/**
 * Constant representing the preference that controls the display options for non-performance payment.
 * @type {string}
 */
export const VENDOR_PAYMENT_DISPLAY: string = 'vendor_payment_display';

/**
 * Constant representing the preference that controls the access to non-performance payment.
 * @type {string}
 */
export const VENDOR_PAYMENT_ACCESS: string = 'vendor_payment_access';

/**
 * Constant representing the default preference that defines the access to non-performance payment.
 * @type {string}
 */
export const VENDOR_PAYMENT_ACCESS_DEFAULT: string = '1';

/**
 * Constant representing the customer display option in the payment for non-performance.
 * @type {string}
 */
export const PAYER_PAYMENT_OPTIONS: string = 'payer_payment_options';

/**
 * Represents a custom field
 * @constructor
 */
export class CustomField {
	/**
	 * The unique identifier of the custom field.
	 *
	 * @type {number}
	 */
	id: number;

	/**
	 * The label of the custom field.
	 *
	 * @type {string}
	 */
	label: string;

	/**
	 * The name of the custom field.
	 *
	 * @type {string}
	 */
	name: string;

	/**
	 * Additional instructions for filling out the custom field.
	 *
	 * @type {string | undefined}
	 */
	instruction: string | undefined;

	/**
	 * Indicates if the custom field is required.
	 *
	 * @type {boolean}
	 */
	required: boolean;

	/**
	 * Indicates if the custom field value should be encrypted.
	 * @type {boolean}
	 */
	encrypt: boolean;

	/**
	 * The default value of the custom field.
	 * @type {string | undefined}
	 */
	defaultValue: string | undefined;

	/**
	 * The display value of the custom field.
	 * @type {string | undefined}
	 */
	displayValue: string | undefined;

	/**
	 * The minimum number of characters allowed for the custom field value.
	 * @type {number}
	 */
	charMin: number;

	/**
	 * The maximum number of characters allowed for the custom field value.
	 * @type {number}
	 */
	charMax: number;

	/**
	 * The sorting order of the custom field in the display.
	 * @type {number}
	 */
	displaySort: number;

	/**
	 * Indicates if the custom field is for admin use only.
	 * @type {boolean}
	 */
	forAdmin: boolean;

	/**
	 * The number of unmasked characters in the custom field value.
	 * @type {number}
	 */
	unmaskedChars: number;

	/**
	 * The type of the custom field.
	 * @type {CustomFieldType}
	 */
	type: CustomFieldType;

	/**
	 * The list of choices available for the custom field.
	 * @type {Array<CustomFieldChoice>}
	 */
	choiceList: Array<CustomFieldChoice>;

	/**
	 * Indicates if the custom field is displayed.
	 * @type {boolean}
	 */
	isDisplayed: boolean;

	/**
	 * The form control associated with the custom field.
	 * @type {FormControl | undefined}
	 */
	formControl: FormControl | undefined;

	/**
	 * Initializes the custom field object.
	 */
	constructor() {
		// ETT-375 Properties are required for custom fields display.
		if (! Object.prototype.hasOwnProperty.call(this, 'isDisplayed')) {
			this.isDisplayed = true;
		}
	}
}


export class CustomFieldType {
	id: number;
	groupAlias: string;
	alias: string;
	name: string;
}

export class CustomFieldChoice {
	id: string;
	name: string;
	group: boolean;
	children?: CustomFieldChoice[];
}

export class CustomFieldValue {
	id?: number;
	moduleId?: number;
	moduleValue?: string;

	fieldId: number;
	fieldLabel: string;
	fieldName: string;
	fieldTypeAlias: string;
	fieldUnmaskedChars: number;

	// ETT-88 Expand the dynamic select for Place of Performance with states list.
	values: string;
	valuePreview: string;

	userId?: number;
}

export class CustomFieldSelectOption {
	id: string;
	text: string;
	disabled?: boolean;
	children?: Array<CustomFieldSelectOption>;
	additional?: any;
}
